import React, { useMemo, useState, useEffect } from 'react';
import classnames from 'classnames';
import { getLocale, useIntl } from 'umi';
import TypeSelect from './components/TypeSelect';
import SingleCountrySelect from './components/SingleCountrySelect';
import MultipleCountrySelect from './components/MultipleCountrySelect';
import useLocaleClassName from '../../pages/TopCharts/_hooks/useLocaleClassName';
import RegionCascaderSelect from '@home/components/RegionCascaderSelect';
import NewRegionSelect from '@home/components/NewRegionSelect';
import { getCountryFlag } from '@/pages/home/_config/countries';
import { analysisRegionsCountriesData } from './utils';
import { TYPES_MAPPING } from './constants';
import './index.scss';

import type { SelectProps } from 'tea-component';
import type { IBaseProps, IRegion } from '@/pages/intelligence/types/common';
import type { IComposedCountryValue, IType } from './types';
import { formatRegionOptions } from '@intelligence/components/RegionSelector';

interface IComposedCountrySelectProps extends IBaseProps {
  title?: string;
  // 是否展示类型选择
  isShowTypeSelect?: boolean;
  /** 单选时使用地区-国家两级级联选择器。 */
  useRegionCascaderForSingle?: boolean;
  /** 多选时使用地区-国家两级选择器。 */
  useRegionSelectorForMultiple?: boolean;
  // 是否开启 搜索关键字 高亮显示
  highlightSearchKeywords?: boolean;
  defaultSingleCountry?: string;
  disabled?: boolean;
  disabledLabel?: string;
  footer?: SelectProps['footer'];
  options: IRegion[];
  isAll: boolean;
  value: IComposedCountryValue;
  onChange: (payload: IComposedCountryValue) => void;
  onTypeChange?: (payload: IType) => void;
  onCountryChange?: (typePayload: IType, countryPayload: string[]) => void;
}

const ComposedCountrySelect: React.FC<IComposedCountrySelectProps> = (props) => {
  const {
    style,
    className,
    title = 'dbi_country',
    isShowTypeSelect = true,
    useRegionCascaderForSingle = false,
    useRegionSelectorForMultiple,
    highlightSearchKeywords = true,
    defaultSingleCountry = 'us',
    disabled = false,
    disabledLabel = '',
    footer,
    options: optionsOuter,
    isAll,
    value: valueOuter,
    defaultValue,
    onChange,
    onTypeChange,
    onCountryChange,
  } = props;

  const locale = getLocale();
  const { formatMessage } = useIntl();
  const localeClassName = useLocaleClassName();

  const { groups, options, values } = analysisRegionsCountriesData(optionsOuter, locale);
  const { countryCode, regionOptions } = useMemo(() => formatRegionOptions(optionsOuter), [optionsOuter]);

  const [searchKeywords, setSearchKeywords] = useState('');
  const handleSearch = (payload?: string) => {
    const keywordsTmp = payload ?? '';
    setSearchKeywords(keywordsTmp);
  };
  const handleClose = () => {
    setSearchKeywords('');
  };

  const renderOptionName = (name: string, code: string) => {
    if (!highlightSearchKeywords) return name;
    if (!searchKeywords?.trim()?.length) return name;
    // 单选、并且 当前option就是选中项时，不高亮
    if (value?.type === TYPES_MAPPING.SINGLE && value?.countries?.[0] === code) return name;
    const searchKeywordsChars = searchKeywords?.split('')?.filter((item) => item);
    const reg = new RegExp(searchKeywordsChars?.join('|'), 'ig');
    return name?.replace(reg, (item) => `<span class="dbi-country-highlight">${item}</span>`);
  };

  const optionsPlus = useMemo(() => {
    if (!Array.isArray(options)) return [];
    return options?.map((option) => {
      const countryCode = option?.value ?? '';
      const countryName = option?.text ?? '';
      return {
        ...option,
        name: countryName,
        text: (
          <div className="dbi-filter-selector-option-node">
            <img
              className="dbi-filter-selector-option-node-icon"
              src={getCountryFlag(countryCode)}
              alt=""
            />
            <span
              className="dbi-filter-selector-option-node-text"
              dangerouslySetInnerHTML={{
                __html: renderOptionName(countryName as string, countryCode),
              }}
            />
          </div>
        ),
      };
    });
  }, [locale, optionsOuter]);

  const [value, setValue] = useState<IComposedCountryValue>({
    type: TYPES_MAPPING.MULTIPLE,
    countries: values,
  });
  const handleTypeChange = (payload: IType) => {
    const countriesTmp = (() => {
      if (payload === TYPES_MAPPING.MULTIPLE) return values;
      if (defaultSingleCountry && values?.includes(defaultSingleCountry))
        return [defaultSingleCountry];
      return values?.slice(0, 1);
    })();
    const valueTmp = {
      ...value,
      type: payload,
      countries: countriesTmp,
    };
    setValue(valueTmp);

    onChange(valueTmp);
    onTypeChange?.(payload);
  };

  const handleSingleCountryChange = (payload: string) => {
    const countriesTmp = payload ? [payload] : [];
    const valueTmp = {
      ...value,
      countries: countriesTmp,
    };
    setValue(valueTmp);
    onChange(valueTmp);
    onCountryChange?.(valueTmp?.type, countriesTmp);
  };

  const handleMultipleCountryConfirm = (payload: string[]) => {
    const valueTmp = {
      ...value,
      countries: payload,
    };
    setValue(valueTmp);
    onChange(valueTmp);
    onCountryChange?.(valueTmp?.type, payload);
  };

  const isAllCountriesSelected =
    countryCode.length > 0 && countryCode.every((country) => value?.countries?.includes(country));
  const multipleRegionValue = {
    type: 'market',
    regions: [],
    markets: isAllCountriesSelected ? ['global'] : value?.countries ?? [],
  };
  const handleMultipleRegionClose = ({ markets = [] }: { markets?: string[]; }) => {
    const countries = markets[0] === 'global' || !markets.length ? countryCode : markets;
    if (JSON.stringify(countries) !== JSON.stringify(value?.countries)) {
      handleMultipleCountryConfirm(countries);
    }
  };

  useEffect(() => {
    const valueTmp = {
      ...value,
      ...valueOuter,
    };

    let filterMarkets = valueOuter?.countries?.filter((item) => countryCode?.includes(item));
    if (valueTmp.type === 'single') {
      filterMarkets = filterMarkets?.length > 0 ? filterMarkets : [defaultSingleCountry];
    } else {
      const countryLength = defaultValue?.value?.countries?.length;
      filterMarkets =
        filterMarkets?.length > 0
          ? filterMarkets
          : countryLength
          ? defaultValue?.value?.countries
          : countryCode;
    }
    valueTmp.countries = filterMarkets;
    setValue(valueTmp);
  }, [JSON.stringify(valueOuter), JSON.stringify(optionsOuter)]);

  return (
    <div
      style={style}
      className={classnames('search-item dbi-composed-country-select', className, localeClassName, {
        'has-type-select': isShowTypeSelect,
      })}
    >
      {isShowTypeSelect && (
        <TypeSelect
          className="dbi-composed-country-select_type-select"
          disabled={disabled}
          value={value?.type}
          onChange={handleTypeChange}
        />
      )}
      {/* {value?.type?value?.type:1} */}
      {/* 单选 国家 */}
      {value?.type === TYPES_MAPPING.SINGLE && useRegionCascaderForSingle && (
        <RegionCascaderSelect
          className="dbi-composed-country-select_country-select dbi-composed-country-select_single-country-select"
          label={title}
          disabled={disabled}
          regions={optionsOuter}
          value={value?.countries?.[0] || defaultValue?.value?.countries?.[0]}
          onChange={handleSingleCountryChange}
        />
      )}
      {value?.type === TYPES_MAPPING.SINGLE && !useRegionCascaderForSingle && (
        <SingleCountrySelect
          className="dbi-composed-country-select_country-select dbi-composed-country-select_single-country-select"
          isShowButtonLabel={!isShowTypeSelect}
          title={title}
          disabled={disabled}
          disabledLabel={disabledLabel}
          groups={groups}
          footer={footer}
          options={optionsPlus}
          value={
            value?.countries?.length === 1
              ? value?.countries?.[0]
              : defaultValue?.value?.countries?.[0]
          }
          onChange={handleSingleCountryChange}
          onSearch={handleSearch}
          onClose={handleClose}
        />
      )}
      {/* 多选 国家 */}
      {value?.type === TYPES_MAPPING.MULTIPLE && useRegionSelectorForMultiple && (
        <NewRegionSelect
          className="dbi-composed-country-select_country-select dbi-composed-country-select_multiple-country-select"
          placeholder={formatMessage({ id: title })}
          locale={locale}
          options={regionOptions}
          radioOptions={[{ value: 'global', text: 'All', textCN: '全部' }]}
          radioOptionsTips={{
            global: formatMessage({ id: 'dbi_info_global_filter' }),
          }}
          shortcutOptions={[]}
          realRegionsList={[]}
          defaultSelectedRegion={isAllCountriesSelected && 'global'}
          value={multipleRegionValue}
          onCloseReginSelect={handleMultipleRegionClose}
        />
      )}
      {value?.type === TYPES_MAPPING.MULTIPLE && !useRegionSelectorForMultiple && (
        <MultipleCountrySelect
          className="dbi-composed-country-select_country-select dbi-composed-country-select_multiple-country-select"
          disabled={disabled}
          title={title}
          renderOptionName={renderOptionName}
          groups={groups}
          options={optionsPlus}
          value={value?.countries}
          onConfirm={handleMultipleCountryConfirm}
          onSearch={handleSearch}
          onClose={handleClose}
          isAll={isAll}
        />
      )}
    </div>
  );
};

export default ComposedCountrySelect;
